import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  FormControl,
  TextField,
  MenuItem,
  InputLabel,
  Select,
} from "@mui/material";
import { Autocomplete } from "@mui/lab";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";

const UserListForLesson = ({ lessonId, onUserAdded, open, onClose }) => {
  const [users, setUsers] = useState([]);            // last backend response
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");   // what the user types
  const [hasSearched, setHasSearched] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);  // controls whether Autocomplete popup is open

  const [selectedUser, setSelectedUser] = useState(null);
  const [passes, setPasses] = useState([]);
  const [selectedPass, setSelectedPass] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // When dialog opens, fetch all users once; when it closes, reset everything.
  useEffect(() => {
    if (open) {
      fetchUsers("");
    }
    if (!open) {
      setSearchTerm("");
      setHasSearched(false);
      setSelectedUser(null);
      setSelectedPass(null);
      setPasses([]);
      setError("");
      setPopupOpen(false);
    }
  }, [open]);

  /**
   * Fetch users from the backend.
   * If searchValue !== "", append &search=full_name={searchValue}.
   * On success, overwrite `users` & `filteredUsers`, set hasSearched, and open the popup.
   */
  const fetchUsers = async (searchValue = "") => {
    setLoading(true);
    setError("");
    try {
      // size is explicit because the backend otherwise pages at 1200 rows, which
      // silently hid every user sorting to the end of the alphabet.
      let url = `${process.env.REACT_APP_BACKEND_ADDRESS}/user/?sort=full_name&size=100000`;
      if (searchValue.trim() !== "") {
        const encoded = encodeURIComponent(searchValue.trim());
        url += `&search=full_name=${encoded}`;
      }

      const headers = await getAuthHeaders();
      const response = await axios.get(url, {
        headers,
      });

      if (response.status === 200 && Array.isArray(response.data.objects)) {
        setUsers(response.data.objects);
        setFilteredUsers(response.data.objects);
        setHasSearched(searchValue.trim() !== "");
        // Open the dropdown so the user sees all results immediately
        setPopupOpen(true);
      } else {
        console.error("Unexpected users response:", response);
        setFilteredUsers([]);
        setPopupOpen(true);
      }
    } catch (err) {
      console.error("Failed to fetch users", err);
      if (err.response && (err.response.status === 403 || err.response.status === 401)) {
        setError("Brak uprawnień do pobrania użytkowników");
      } else {
        setError("Failed to load users");
      }
      setFilteredUsers([]);
      setPopupOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // When a user is picked, fetch their passes for this lesson
  const fetchPasses = async (userId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/pass/all_for_admin/?user_id=${userId}&lesson_id=${lessonId}`,
        { headers }
      );
      if (response.status === 200 && Array.isArray(response.data.objects)) {
        // Filter out passes with 0 remaining lessons (excluding unlimited passes)
        const validPasses = response.data.objects.filter(pass => {
          // Check if pass has any lesson categories with remaining lessons or is unlimited
          return pass.lesson_categories_for_pass?.some(category => 
            category.is_unlimited || category.remaining_lessons > 0
          );
        });
        setPasses(validPasses);
      } else {
        console.error("Unexpected passes response:", response);
        setPasses([]);
      }
    } catch (err) {
      console.error("Failed to fetch passes", err);
      if (err.response && (err.response.status === 403 || err.response.status === 401)) {
        console.error("Brak uprawnień do pobrania karnetów");
      }
      setPasses([]);
    }
  };

  // Autocomplete selection handler
  const handleUserChange = (event, value) => {
    if (value) {
      setSelectedUser(value.id);
      fetchPasses(value.id);
    } else {
      setSelectedUser(null);
      setPasses([]);
      setSelectedPass(null);
    }
  };

  // Add the selected user to the lesson
  const handleAddUserToLesson = async () => {
    if (!selectedUser) return;
    setLoading(true);
    setError("");
    try {
      const selectedUserData = users.find((u) => u.id === selectedUser);
      if (!selectedUserData) {
        throw new Error("Selected user not found in list");
      }

      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user_to_lesson/`,
        {
          email: selectedUserData.email,
          phone: selectedUserData.phone,
          name: selectedUserData.full_name.split(" ")[0],
          surname: selectedUserData.full_name.split(" ")[1] || "",
          lesson_id: lessonId,
          full_name: selectedUserData.full_name,
          pass_id: selectedPass,
          admin_bypass_debt: true,
          acknowledged_no_payment: true,
        },
        {
          headers,
        }
      );

      // Forward the full response body to the caller so it can show a
      // signup-result snackbar (pass exhausted, debt created, etc.).
      onUserAdded(selectedUserData, response.data);
      handleClose();
    } catch (err) {
      if (err.response && err.response.status === 403) {
        setError("Brak uprawnień do dodania użytkownika do zajęć");
      } else if (err.response && err.response.status === 401) {
        setError("Brak uprawnień - zaloguj się ponownie");
      } else if (err.response && err.response.status === 409) {
        setError("Użytkownik jest już zapisany na te zajęcia");
      } else if (err.response && err.response.status === 402) {
        setError("Użytkownik ma nieopłacone długi - to nie powinno się zdarzyć z obejściem przez admina");
      } else if (err.response && err.response.status === 422) {
        // Display the detailed error message from the backend
        setError(err.response.data.detail || "Wszystkie zajęcia w karnecie zostały już wykorzystane");
      } else {
        setError("Nie udało się dodać użytkownika do zajęć");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    // The useEffect above will reset all local state when `open` flips to false.
  };

  // If Enter is pressed in the text field, run fetchUsers(searchTerm)
  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      fetchUsers(searchTerm);
    }
  };

  // When “Clear Filters” is clicked:
  // • reset searchTerm
  // • set hasSearched=false
  // • fetch all users (fetchUsers(""))
  const handleClearFilters = () => {
    setSearchTerm("");
    setHasSearched(false);
    fetchUsers("");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ 
        style: { 
          height: "80vh",
          margin: { xs: 8, sm: 32 },
          maxHeight: { xs: 'calc(100% - 16px)', sm: '80vh' }
        } 
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div">
          Dodaj użytkownika do zajęć
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1, sm: 3 } }}>
        {/* ─── Search + Autocomplete + Buttons ───────────────────────────────── */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          {/* Mobile: Stack vertically, Desktop: Row layout */}
          <Box sx={{ 
            display: "flex", 
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1, sm: 1 }
          }}>
            <Autocomplete
              sx={{ 
                flexGrow: 1,
                minWidth: { xs: "100%", sm: "300px" }
              }}
              options={filteredUsers}
              getOptionLabel={(option) => option.full_name}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ width: "100%", p: { xs: 1, sm: 2 } }}>
                  <Box display="flex" flexDirection="column" sx={{ width: "100%" }}>
                    <Typography 
                      variant="body1"
                      sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                    >
                      {option.full_name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="textSecondary"
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                    >
                      {option.email}
                    </Typography>
                  </Box>
                </Box>
              )}
              open={popupOpen}
              onOpen={() => setPopupOpen(true)}
              onClose={() => setPopupOpen(false)}
              inputValue={searchTerm}
              onInputChange={(event, newInputValue) => {
                setSearchTerm(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Wyszukaj użytkownika"
                  variant="outlined"
                  fullWidth
                  size="medium"
                  onKeyDown={handleSearchKeyDown}
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }
                  }}
                />
              )}
              onChange={handleUserChange}
              disabled={loading}
            />

            <Box sx={{ 
              display: "flex", 
              flexDirection: { xs: "row", sm: "row" },
              gap: 1,
              mt: { xs: 1, sm: 0 }
            }}>
              <Button
                variant="contained"
                onClick={() => fetchUsers(searchTerm)}
                disabled={loading}
                size="medium"
                sx={{ 
                  height: { xs: "40px", sm: "56px" },
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  px: { xs: 2, sm: 3 }
                }}
              >
                Szukaj
              </Button>

              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading}
                size="medium"
                sx={{ 
                  height: { xs: "40px", sm: "56px" },
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  px: { xs: 2, sm: 3 }
                }}
              >
                Wyczyść
              </Button>
            </Box>
          </Box>
        </FormControl>

        {/* ─── Show the active filter (if any) ───────────────────────────────── */}
        {hasSearched && searchTerm.trim() !== "" && (
          <Typography variant="caption" color="textSecondary" sx={{ mb: 2 }}>
            Filtering by full_name=&ldquo;{searchTerm.trim()}&rdquo;
          </Typography>
        )}

        {/* ─── Pass Selection ──────────────────────────────────────────────── */}
        {selectedUser && passes.length > 0 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel 
              id="pass-select-label"
              sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
            >
              Wybierz karnet
            </InputLabel>
            <Select
              labelId="pass-select-label"
              value={selectedPass}
              onChange={(e) => setSelectedPass(e.target.value)}
              label="Wybierz karnet"
              fullWidth
              size="medium"
              sx={{
                '& .MuiSelect-select': {
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                },
                '& .MuiMenuItem-root': {
                  minHeight: { xs: 'auto', sm: '48px' },
                  py: { xs: 1, sm: 2 }
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: { xs: 200, sm: 300 },
                    '& .MuiMenuItem-root': {
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      py: { xs: 1, sm: 1.5 },
                      px: { xs: 2, sm: 3 }
                    }
                  }
                }
              }}
            >
              <MenuItem value={null}>
                <Box display="flex" flexDirection="column" sx={{ py: { xs: 0.5, sm: 1 } }}>
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  >
                    Brak karnetu
                  </Typography>
                </Box>
              </MenuItem>
              {passes.map((pass) => (
                <MenuItem key={pass.id} value={pass.id}>
                  <Box display="flex" flexDirection="column" sx={{ py: { xs: 0.5, sm: 1 }, width: '100%' }}>
                    <Typography 
                      variant="body1"
                      sx={{ 
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        fontWeight: 500
                      }}
                    >
                      {pass.pass_type_name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="textSecondary"
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                    >
                      Ważny do: {new Date(pass.valid_until).toLocaleDateString('pl-PL')}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* ─── Any Error Message ────────────────────────────────────────────── */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
        <Button 
          onClick={handleClose} 
          color="error"
          size="large"
          sx={{ 
            fontSize: { xs: '0.9rem', sm: '1rem' },
            px: { xs: 2, sm: 3 }
          }}
        >
          Anuluj
        </Button>
        <Button
          onClick={handleAddUserToLesson}
          color="primary"
          variant="contained"
          disabled={loading || !selectedUser}
          size="large"
          sx={{ 
            fontSize: { xs: '0.9rem', sm: '1rem' },
            px: { xs: 2, sm: 3 }
          }}
        >
          {loading ? <CircularProgress size={20} /> : "Dodaj użytkownika"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserListForLesson;
